import { getSimilarGames as fetchSimilarGamesZone } from '@/lib/games-recommender';
import type { PRISMIC_ZONE_LAYOUT } from '@/app/constants/zone';
import type { Region } from '@/types/constants';
import type { Game } from '@/types/games';
import type { Zone } from '@/types/zones/zone';
import type { SimilarGamesSlice } from '@/types/zones/slices/similar-games';
import { getZoneGames } from './zone-games';
import type { ClientGameContext } from './client-game-context';

export const MIN_SIMILAR_GAMES = 3;
export const MAX_SIMILAR_GAMES = 15;

export type SimilarGamesZoneMeta = {
    title?: string;
    categoryUid?: string;
    zoneUid?: string;
    isDynamicOrdering: boolean;
    anchorGameUid?: string;
};

export type SimilarGamesZoneData = {
    games: ReturnType<typeof getZoneGames>['games'];
    tracking: ReturnType<typeof getZoneGames>['tracking'];
    meta: SimilarGamesZoneMeta;
};

export const getSimilarGamesZone = (
    anchorGameUid: string,
    region: Region,
    channel: string = 'desktop'
): Promise<Zone | null> => fetchSimilarGamesZone(anchorGameUid, region, channel);

const filterSimilarGameItems = (items: Game[], recentGameUids: string[]): Game[] =>
    items.filter((item) => !recentGameUids.includes(item.uid)).slice(0, MAX_SIMILAR_GAMES);

export const parseSimilarGamesZone = (
    zone: Zone,
    gridLayout: PRISMIC_ZONE_LAYOUT,
    ctx: ClientGameContext,
    recentGameUids: string[] = []
): SimilarGamesZoneData => {
    const rawItems = (zone.data.body[0]?.items as Game[] | undefined) ?? [];
    const items = filterSimilarGameItems(rawItems, recentGameUids);
    const zoneGames = getZoneGames(items, gridLayout, ctx);

    return {
        games: zoneGames.games,
        tracking: zoneGames.tracking,
        meta: {
            title: zone.data.title?.[0]?.text,
            categoryUid: zone.data.body[0]?.primary?.category?.uid,
            zoneUid: zone.uid,
            isDynamicOrdering: zone.data.dynamic_zone ?? false,
            anchorGameUid: (zone.data.body[0] as SimilarGamesSlice | undefined)?.anchor_game_uid
        }
    };
};

export const getSimilarGamesZoneData = async (
    anchorGameUid: string,
    gridLayout: PRISMIC_ZONE_LAYOUT,
    ctx: ClientGameContext,
    recentGameUids: string[] = [],
    channel: string = 'desktop'
): Promise<SimilarGamesZoneData | null> => {
    if (!anchorGameUid) {
        return null;
    }

    const zone = await getSimilarGamesZone(anchorGameUid, ctx.region, channel);

    if (!zone || (zone.data.body[0]?.items?.length ?? 0) < MIN_SIMILAR_GAMES) {
        return null;
    }

    return parseSimilarGamesZone(zone, gridLayout, ctx, recentGameUids);
};
